import React, { ReactElement, useMemo, Fragment, useEffect } from 'react';
import * as _ from "lodash";
import { useSelector } from 'react-redux';

import styled from 'styled-components/macro';
import { Icon } from '@fluentui/react/lib/Icon';

import { ResourceStrings } from '../../ResourceStrings';
import { RootState } from '../../store/rootState';
import PageHeader from '../../components/Post/PageHeader';
import { forMinWidth } from '../../theme/breakpoints';
import { partiallyDisguiseEmail } from '../../utils/tools';
import { getSpaceConfig, instance, instanceCssClassName, server } from '../../config/serverConfig';
import { SpaceId } from '../../config/spaces/spaceIds';
import { SpaceConfig } from '../../config/spaces/spaceConfig';
import { Alert, AlertType } from '../../AnnouncementInfo';
import { OperationState } from '../../store/appState';
import * as Actions from '../../store/actions';
import { useLocation } from 'react-router';
import { getSpaceIdFromSlug } from '../../config/serverConfig';
import { getPotentialSlugFromPathName } from '../../utils/webPage';

export default function SiteHeader(): ReactElement {
    const userEmail = useSelector((state: RootState) => state.appState.userEmail);
    const state = useSelector((state: RootState) => state.appState);
    const location = useLocation();
    const outageList: Alert[] = state.outage.concat(state.customOutage);
    const degradedSymbol = "⚠";

    function computeOutageSymbol(alertType: AlertType): JSX.Element {
        if (alertType === AlertType.Outage) {
            //The following JSX tags recreate the outage symbols
            return (<svg id="unhealthySvg122" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" width="10.5" height="10" viewBox="0 -0.5 21 21" className="sm-status-icon"><circle cx="10.5" cy="10.5" r="10" fill="#da3b01"></circle>
                <g><path d="M15.09,5.9A1.66,1.66,0,0,0,12.88,6l-.16.15L6.16,12.72,6,12.89a1.65,1.65,0,0,0-.1,2.21A1.66,1.66,0,0,0,8.12,15l.16-.15,6.56-6.56L15,8.11A1.65,1.65,0,0,0,15.09,5.9Z" fill="#fff"></path>
                    <path d="M5.9,5.91A1.66,1.66,0,0,0,6,8.12l.15.16,6.56,6.56.17.15a1.65,1.65,0,0,0,2.21.1A1.66,1.66,0,0,0,15,12.88l-.15-.16L8.28,6.16,8.11,6A1.65,1.65,0,0,0,5.9,5.91Z" fill="#fff"></path></g></svg>);
        }
        else if (alertType === AlertType.Degraded) {
            return (<>{degradedSymbol}</>);
        }
        else if (alertType === AlertType.Advisory) {
            return (<svg id="infoSvg120" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" width="10.5" height="10" viewBox="0 -0.5 21 21" className="sm-status-icon"><circle cx="10.5" cy="10.5" r="10" fill="#0067b7"></circle>
                <g><path d="M10.5,6.06A1.5,1.5,0,1,0,9,4.56,1.5,1.5,0,0,0,10.5,6.06Z" fill="#fff"></path>
                    <path d="M10.5,17.94A1.53,1.53,0,0,0,12,16.55V8.32a1.49,1.49,0,0,0-3,0v8.23A1.53,1.53,0,0,0,10.5,17.94Z" fill="#fff"></path></g></svg>);
        }
        else
            return (<></>);
    }

    function computeOutageType(alertType: AlertType): JSX.Element {
        if (alertType === AlertType.Outage)
            //The following JSX tags recreate the outage symbols
            return (<>&nbsp;Outage:&nbsp;</>);
        else if (alertType === AlertType.Degraded)
            return (<>&nbsp;Degraded:&nbsp;</>);
        else if (alertType === AlertType.Advisory)
            return (<>&nbsp;Advisory:&nbsp;</>);
        else
            return (<></>);
    }


    useEffect(() => {
        Actions.loadOutage();
        Actions.loadCustomOutage();
    }, []);

    // keep the banner's code in case we need it again in future
    const showBanner = false;
    const showMaintenanceBanner = false;
    const showOutageIfAny = true; // this allows easy deactivation of front end display in cases of failure

    const spaceConfig: SpaceConfig = useMemo(() => {
        const maybeSlug: string = getPotentialSlugFromPathName(location.pathname);
        const spaceId: SpaceId | null = getSpaceIdFromSlug(maybeSlug);
        return getSpaceConfig(spaceId ?? SpaceId.VS); // @TODO: nikolami: 2022-10-14: consider having "default" space instead of SpaceId.VS for base colors and such?
    }, [location]);

    return <Wrapper>
        <Header aria-label="Site Header" overrideHeaderColor={spaceConfig.accentColor} isInternal={instance.isInternal}>
            <h1 className={`top-level-title-wrapper ${instanceCssClassName}`} css="font-weight: 600">{server.website?.title ?? ResourceStrings.developerCommunity()}</h1>
            <div className={`top-level-subtitle-wrapper ${instanceCssClassName}`}>
                <div className="top-level-subtitle">{instance.isInternal ? "Internal Feedback" : "Your open channel to Microsoft engineering teams"}</div>
                {userEmail && !instance.isInternal && <span className="viewing-as">
                    <Icon iconName="UserFollowed" />
                    {ResourceStrings.viewingPageUsingAccount(partiallyDisguiseEmail(userEmail))}
                </span>}
            </div>
            {/* <div css="font-size: 1.2rem">Share feedback on the pilot <a target="_blank" href="https://aka.ms/oecfeedback">here</a></div> */}
        </Header>
        {
            (state.customOutageLoadState === OperationState.Failed) && <>
                <div className="custom-outage-bar-loading"
                    aria-label={ResourceStrings.errorLoadingCustomOutage(state.customOutageErrorMessage)}>
                    {ResourceStrings.errorLoadingCustomOutage(state.customOutageErrorMessage)}
                </div>
            </>
        }
        {
            (state.outageLoadState === OperationState.Failed) && <>
                <div className="outage-bar-loading"
                    aria-label={ResourceStrings.errorLoadingOutage(state.outageErrorMessage)}>
                    {ResourceStrings.errorLoadingOutage(state.outageErrorMessage)}
                </div>
            </>
        }
        {
            (state.outageLoadState === OperationState.Success || state.customOutageLoadState === OperationState.Success)
            && (outageList != null) && (outageList.length > 0) && showOutageIfAny && <>
                <OutageBanner>
                    <div css="background: var(--color-outage-background); color: var(--color-text); padding: 0rem;">
                        <ol className="mylist" css="padding-left: 0">
                            {(outageList || []).map((alert, i) => <Fragment key={i}>
                                <li css="border-bottom:0.1rem solid var(--color-gray); text-align: left; padding: 0.3rem">
                                    &nbsp;  {computeOutageSymbol(alert.alertType)} {' '}
                                    {computeOutageType(alert.alertType)}
                                    <a href={alert.url}
                                        key={i}
                                        className="outageLink"
                                        tabIndex={0}
                                        aria-label={alert.title}
                                        role="option"> {alert.title}
                                    </a> <span className="description">&nbsp;&nbsp;{alert.description}</span>
                                </li>

                            </Fragment>
                            )}
                        </ol>
                    </div>
                </OutageBanner>
            </>
        }
        {showBanner && <Banner>
            <div>
                <div className="top-banner-highlight">Visual Studio 2022 Launch</div>
                <div>Join us November 8th for the virtual launch event</div>
                <div><a href="https://aka.ms/vs2022launch" target="_blank" rel="noopener noreferrer">Save the date <Icon className="icon" iconName="ChevronRight" /></a></div>
            </div>
        </Banner>}
        {showMaintenanceBanner && <Banner>
            <div css="background: var(--color-outage-background); color: black;">
                <div css="font-weight: bold">⚠ Visual Studio Feedback Experience Degraded </div>
                {/*<div>All feedback will be<b>&nbsp;read-only&nbsp;</b>all day Tuesday, March 29, 2022: 9 AM - 6 PM PST</div>*/}
                <div>We are currently experiencing issues with the VS Feedback system. We are in the process of investigating this and will report more details soon.</div>
            </div>
        </Banner>}
    </Wrapper>



}

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
`;

const Header = styled(PageHeader)`
    display: flex;
    flex-direction: column;
    justify-content: center;

    .top-level-text {
        width: 100%;
        font-size: 3.8rem;
        font-weight: 600;
        line-height: 6rem;
        padding-bottom: 0.5rem;
    }

    .top-level-subtitle-wrapper {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 0.6rem;

        .top-level-subtitle {
            font-size: 2.2rem;
            font-weight: normal;
        }

        .viewing-as {
            font-size: 1.4rem;
            font-weight: normal;
            position: relative;
            top: -0.1em;
            margin-top: 1rem;

            i {
                font-size: 1.5rem;
                vertical-align: middle;
                padding-right: 1rem;
            }

            ${forMinWidth.lg`
                margin-top: 0;
            `}
        }

        ${forMinWidth.lg`
            padding-top: 0;
            padding-bottom: 0;
        `}
    }

    ${forMinWidth.lg`
        .top-level-subtitle-wrapper {
            flex-direction: row;
            align-items: flex-end;
        }
    `}
`;

const Banner = styled.div`
    background-color: var(--color-text);
    color: white;
    border-bottom: solid 1px #2e2544;

    & > div {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        align-items: flex-start;
        padding: 1.6rem 1rem;
        font-size: 1.4rem;
        line-height: 2rem;
    }

    & > div > div {
        display: flex;
    }

    @media (max-width: 1099px) {
        & > div > * + * {
            margin-top: .6rem;
        }
    }

    .top-banner-highlight {
        font-size: 1.8rem;
        line-height: 2.2rem;
        font-weight: 600;
    }

    a {
        text-decoration: none;
        background-color: white;
        color: var(--color-slight-black);
        padding: .5rem 1rem;
        font-weight: bold;
        white-space: nowrap;
        border-radius: .2rem;
    }

    a:focus {
        outline: solid 1px var(--color-specific-text);
        outline-offset: -3px;
    }

    @media (min-width: 800px) {
        & > div {
            padding: 1.6rem 15%;
        }
    }

    @media (min-width: 1100px) {
        & > div {
            flex-direction: row;
            align-items: center;
        }

        & > div > * + * {
            margin-left: 2rem;
        }
    }
`;

const OutageBanner = styled.div`
    background-color: var(--color-text);
    color: white;

    & > div {
        font-size: 1.2rem;
        line-height: 2rem;

    }
    a {
        color: var(--color-links);
        white-space: nowrap;
        border-radius: .2rem;
    }
    }
`;